/**
 * Get-the-template panel. Download CTA + the required/optional column
 * list. Pure presentation; the parent owns wizard state and routes
 * "Next" via `onNext`.
 */

import React from 'react';
import Button from '../../../components/Button/Button';
import { ModalActions } from '../../../components/Modal/Modal';
import { trackImportTemplateDownloaded } from '../../../analytics';
import { OPTIONAL_FIELDS, REQUIRED_FIELDS, TEMPLATE_CSV, TEMPLATE_FILE_NAME } from './constants';
import { CheckIcon, DownloadIcon, FileIcon } from './icons';
import styles from '../Integrations.module.scss';

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = TEMPLATE_FILE_NAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer the revoke — some browsers (notably older Safari) start the
  // download asynchronously after `.click()`, and revoking immediately
  // can cancel an in-flight save. A 0ms timeout pushes the revoke past
  // the current task so the download has time to latch onto the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  trackImportTemplateDownloaded();
}

interface TemplateIntroProps {
  onCancel: () => void;
  onNext: () => void;
}

export default function TemplateIntro({ onCancel, onNext }: TemplateIntroProps) {
  return (
    <div className={styles.wizardStep}>
      <div className={styles.templateRow}>
        <div className={styles.templateIcon}>
          <FileIcon />
        </div>
        <div className={styles.templateMeta}>
          <div className={styles.templateName}>{TEMPLATE_FILE_NAME}</div>
          <div className={styles.templateDesc}>Header row + two example rows</div>
        </div>
        <Button variant="secondary" size="sm" onClick={downloadTemplate}>
          <DownloadIcon />
          Download template
        </Button>
      </div>

      <div className={styles.compatLine}>
        Your CSV must start with a <strong>header row</strong> using these column names. Column order is up to you — we
        match each value to the field by name, not position.
      </div>

      <div className={styles.fieldSectionLabel}>
        Required
        <span className={styles.fieldSectionHint}>
          {REQUIRED_FIELDS.length} field{REQUIRED_FIELDS.length === 1 ? '' : 's'} · names must match (any order)
        </span>
      </div>
      <div className={styles.plainFields}>
        {REQUIRED_FIELDS.map((f) => (
          <div key={f.colname} className={styles.plainField}>
            <div className={styles.plainFieldCheck}>
              <CheckIcon />
            </div>
            <span className={styles.plainFieldLabel}>{f.label}</span>
            <span className={styles.plainFieldColname}>{f.colname}</span>
          </div>
        ))}
      </div>

      <details className={styles.optionalToggle}>
        <summary>
          <span>Show {OPTIONAL_FIELDS.length} optional fields (breed, birthday, weight, color, owner address…)</span>
        </summary>
        <div className={styles.plainFields}>
          {OPTIONAL_FIELDS.map((f) => (
            <div key={f.colname} className={styles.plainField}>
              <span className={styles.plainFieldLabel} style={{ marginLeft: 24 }}>
                {f.label}
              </span>
              <span className={styles.plainFieldColname}>{f.colname}</span>
            </div>
          ))}
        </div>
      </details>

      <div className={styles.compatLine}>
        Any 9, 10, or 15-character hex microchip serial (Fi Nano, HomeAgain, AVID…) · chips already in your inventory
        are re-linked, unclaimed chips are registered to your rescue, and chips registered to another rescue are
        rejected.
      </div>

      <ModalActions>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="green" onClick={onNext}>
          Next: Upload
        </Button>
      </ModalActions>
    </div>
  );
}
